// ═══════════════════════════════════════
// ATLAS BEASTS — LAUNCH CHARACTER ROSTER
// Ploonu LLC — Confidential
// ═══════════════════════════════════════

import type { Character } from '../types'

export const LAUNCH_CHARACTERS: Character[] = [
  {
    id: 'kai-voss',
    name: 'Kai Voss',
    title: 'The Currentblade',
    faction: 'drifters',
    class: 'currentblade',
    element: 'fire',
    zone: 'ashwake',
    rarity: 'launch',
    stats: { attack: 84, defense: 58, speed: 71, special: 76 },
    moves: [
      { name: 'Current Slash',  type: 'melee',   element: 'fire',  power: 62,  description: 'A fast diagonal slash that leaves a fire current trail.' },
      { name: 'Tide Break',     type: 'charge',  element: 'fire',  power: 88,  description: 'Charges forward through the opponent releasing stored current.' },
      { name: 'Shard Drive',    type: 'special', element: 'fire',  power: 110, description: 'Drives the Tide Shard blade into the ground releasing a radial burst.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'rell',
    backstory: 'A navigator who survived the destruction of his island by accidentally absorbing a Tide Well shard. Has no destination. Just forward.',
    quote: 'The sea took everything from me. So I took it back.',
  },
  {
    id: 'sela-mourne',
    name: 'Sela Mourne',
    title: 'The Tidecaller',
    faction: 'wrecked',
    class: 'tidecaller',
    element: 'water',
    zone: 'glassdeep',
    rarity: 'launch',
    stats: { attack: 72, defense: 45, speed: 68, special: 95 },
    moves: [
      { name: 'Grief Tide',     type: 'ranged',  element: 'water', power: 58,  description: 'Summons water tendrils from the deep that grab and pull.' },
      { name: 'Still Waters',   type: 'control', element: 'water', power: 70,  description: 'Creates a zone of dead calm that slows opponents.' },
      { name: 'Mourning Surge', type: 'burst',   element: 'water', power: 105, description: 'Releases all contained grief in a tidal wave of force.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'voss-manta',
    backstory: 'Lost her entire crew to an Iron Meridian purge. Now she hunts them with surgical patience. The grief is not weakness — it is the weapon.',
    quote: 'I stopped crying. The ocean does that for me now.',
  },
  {
    id: 'drego',
    name: 'Drego',
    title: 'Harbor King',
    faction: 'drifters',
    class: 'brawler',
    element: 'earth',
    zone: 'rootlands',
    rarity: 'launch',
    stats: { attack: 96, defense: 88, speed: 38, special: 55 },
    moves: [
      { name: 'Harbor Swing',   type: 'melee',   element: 'earth', power: 72,  description: 'Swings the anchor chain in a wide arc.' },
      { name: 'Groundbreak',    type: 'charge',  element: 'earth', power: 90,  description: 'Drives both fists into the ground creating a shockwave.' },
      { name: 'Last Harbor',    type: 'special', element: 'earth', power: 118, description: 'Full anchor chain overhead slam. The ground does not recover.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'crag',
    backstory: 'Former harbor master. Watched his city collapse. The anchor chain is the last thing he took when he left. He chose it.',
    quote: 'The harbor is gone. I am still here. That will have to be enough.',
  },
  {
    id: 'vara-six',
    name: 'Vara Six',
    title: 'Bladestorm',
    faction: 'sovereign-few',
    class: 'bladestorm',
    element: 'earth',
    zone: 'rootlands',
    rarity: 'launch',
    stats: { attack: 88, defense: 62, speed: 78, special: 70 },
    moves: [
      { name: 'Six-Cut',        type: 'melee',   element: 'earth', power: 78,  description: 'All four mechanical arms strike simultaneously from different angles.' },
      { name: 'Gear Drive',     type: 'charge',  element: 'earth', power: 84,  description: 'Mechanical arms rotate at speed creating a drill effect.' },
      { name: 'Iron Ascent',    type: 'special', element: 'earth', power: 112, description: 'Mechanical arms lock into the ground and launch her upward into a spinning descent.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'snip',
    backstory: 'Built her own mechanical arms after losing both in a raid. Did not ask for help. Never does. The extra two arms were not a replacement. They were an upgrade.',
    quote: 'Ask me what I lost. I dare you.',
  },
  {
    id: 'marshal-coen',
    name: 'Marshal Coen',
    title: 'The True Believer',
    faction: 'iron-meridian',
    class: 'commander',
    element: 'earth',
    zone: 'rootlands',
    rarity: 'launch',
    stats: { attack: 75, defense: 95, speed: 44, special: 68 },
    moves: [
      { name: 'Shield Wall',    type: 'control', element: 'earth', power: 55,  description: 'Tower shield raised creates a zone that reduces incoming damage.' },
      { name: 'Cannon Round',   type: 'ranged',  element: 'earth', power: 85,  description: 'Single cannon shot with armor-piercing effect.' },
      { name: 'Rogue Marshal',  type: 'special', element: 'earth', power: 100, description: 'Shield thrown forward then cannon fired through the gap. Costs his insignia.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'warden',
    backstory: 'Spent his life believing the Iron Meridian was salvation. Is now standing at the edge of understanding what it actually is.',
    quote: 'I believed in something. I am still deciding what.',
  },
  {
    id: 'lune',
    name: 'Lune',
    title: 'The Devoted',
    faction: 'tidecallers',
    class: 'mystic',
    element: 'water',
    zone: 'glassdeep',
    rarity: 'launch',
    stats: { attack: 65, defense: 55, speed: 72, special: 98 },
    moves: [
      { name: 'Tidal Shift',    type: 'control', element: 'water', power: 60,  description: 'Staff reshapes — one form heals allies, another disrupts opponents.' },
      { name: 'Deep Pull',      type: 'ranged',  element: 'water', power: 75,  description: 'Draws from the deep to pull opponents off balance.' },
      { name: 'Devotion Wave',  type: 'special', element: 'water', power: 108, description: 'Channels the tide fully — massive healing pulse or devastating strike depending on intent.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'orrin',
    backstory: 'Devout follower of the Tide Wells. Has given everything to something that may not deserve it. Has not stopped giving.',
    quote: 'I know what I am giving myself to. I have made peace with not knowing why.',
  },
  {
    id: 'reef',
    name: 'Reef',
    title: 'The Scout',
    faction: 'drifters',
    class: 'scout',
    element: 'air',
    zone: 'skyrift',
    rarity: 'launch',
    stats: { attack: 70, defense: 48, speed: 96, special: 62 },
    moves: [
      { name: 'Hook Run',       type: 'melee',   element: 'air',   power: 55,  description: 'Fast dash with trailing hook catch on return.' },
      { name: 'Wind Step',      type: 'control', element: 'air',   power: 65,  description: 'Creates a wind displacement forcing position changes.' },
      { name: 'Open Sky',       type: 'special', element: 'air',   power: 95,  description: 'Full speed aerial strike from maximum height. No ceiling.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'flick',
    backstory: 'Running from something that stopped chasing him a long time ago. Has not told his feet yet.',
    quote: 'I run because I want to. Not because anything is behind me. Mostly.',
  },
  {
    id: 'oska-unmade',
    name: 'Oska The Unmade',
    title: 'The Berserker',
    faction: 'wrecked',
    class: 'berserker',
    element: 'void',
    zone: 'null-current',
    rarity: 'launch',
    stats: { attack: 102, defense: 40, speed: 65, special: 88 },
    moves: [
      { name: 'Void Strike',    type: 'melee',   element: 'void',  power: 80,  description: 'Raw void energy punch that corrupts on contact.' },
      { name: 'Fracture',       type: 'burst',   element: 'void',  power: 92,  description: 'Void energy bursts from body cracks — AOE damage.' },
      { name: 'The Unmade',     type: 'special', element: 'void',  power: 125, description: 'Full void release. Maximum damage. Costs his own stability.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'null',
    backstory: 'Went too far. Is trying to find the line again. NULL mirrors his emotional state — when Oska is calm, NULL is small. When Oska breaks, NULL becomes something else.',
    quote: 'I know what I am. I am still deciding what to do about it.',
  },
  {
    id: 'twin-daggers-yara',
    name: 'Twin Daggers Yara',
    title: 'The Contract',
    faction: 'sovereign-few',
    class: 'assassin',
    element: 'air',
    zone: 'skyrift',
    rarity: 'launch',
    stats: { attack: 85, defense: 44, speed: 92, special: 75 },
    moves: [
      { name: 'Smoke Step',     type: 'control', element: 'air',   power: 58,  description: 'MIST creates a feint — Yara repositions unseen.' },
      { name: 'Contract Hit',   type: 'melee',   element: 'air',   power: 82,  description: 'Precision dual strike at highest-value target point.' },
      { name: 'Scarlet Clause', type: 'special', element: 'air',   power: 115, description: 'MIST wraps the target — Yara strikes from every direction simultaneously.' },
    ],
    awakeningStage: 1,
    bondedCreatureId: 'mist',
    backstory: 'Works alone. Except for MIST. The creature never signed a contract. It just never left. That means more to Yara than she will ever say.',
    quote: 'I do not have allegiances. I have a price. MIST was free.',
  },
]
